import React from 'react';

import { Center, Loader, Modal, Stack, createStyles } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ModalTitle } from 'components/RawResourceTextModal/ModalTitle';
import { TextContent } from 'components/RawResourceTextModal/TextContent';
import { ResourceService } from 'services/resource-service';
import { RESOURCE_RAW_TEXT_QUERY_KEY } from 'shared/constants/query-keys';

interface RawResourceTextModalProps {
  resourceId: string;
  opened: boolean;
  onClose: () => void;
  title: string;
}

const useStyles = createStyles((theme) => ({
  modalContent: {
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.lg,
    width: '100%',
    height: '80vh',
    overflow: 'hidden'
  }
}));

export function RawResourceTextModal({ resourceId, opened, onClose, title }: RawResourceTextModalProps) {
  const { classes } = useStyles();

  // When the modal opens, fetch the data
  const { data: rawText, isLoading } = useQuery({
    queryKey: [RESOURCE_RAW_TEXT_QUERY_KEY, { resourceId }],
    queryFn: () => ResourceService.getResourceRawText(resourceId),
    enabled: opened && !!resourceId,
    retry: false,
    // Cache since we don't want to fetch the data again
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  // Process the text into lines only when data changes
  const lines = rawText ? rawText.split('\n') : [];
  const lineCount = lines.length;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      closeOnClickOutside={true}
      closeOnEscape={true}
      title={<ModalTitle title={title} lineCount={lineCount} />}>
      <Stack className={classes.modalContent}>
        {isLoading ? (
          <Center style={{ height: '100%', minHeight: 200 }}>
            <Loader size="md" variant="dots" />
          </Center>
        ) : (
          <TextContent rawText={rawText || ''} lines={lines} lineCount={lineCount} />
        )}
      </Stack>
    </Modal>
  );
}
